import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './StoringDetail.css';
import api from '../services/api';

const StoringDetail = () => {
  const { id } = useParams();
  const { gebruiker, logout } = useAuth();
  const navigate = useNavigate();
  const [storing, setStoring] = useState(null);
  const [fotos, setFotos] = useState([]);
  const [afdelingen, setAfdelingen] = useState([]);
  const [alleAfdelingen, setAlleAfdelingen] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDoorsturenModal, setShowDoorsturenModal] = useState(false);
  const [selectedAfdeling, setSelectedAfdeling] = useState('');

  useEffect(() => {
    loadData();
  }, [id]);

  const loadData = async () => {
    try {
      const [storingRes, afdelingenRes] = await Promise.all([
        api.get(`/storingen/${id}`),
        api.get('/afdelingen')
      ]);
      setStoring(storingRes.data.storing);
      setFotos(storingRes.data.fotos || []);
      
      const afds = afdelingenRes.data.afdelingen || [];
      setAfdelingen(afds);
      
      // Voor doorsturen: haal ALLE afdelingen op
      try {
        const alleAfdsRes = await api.get('/afdelingen/alle');
        setAlleAfdelingen(alleAfdsRes.data.afdelingen || afds);
      } catch {
        setAlleAfdelingen(afds);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Fout bij laden:', error);
      setLoading(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/storingen/${id}`, { status: newStatus });
      setStoring({ ...storing, status: newStatus });
      alert('✅ Status bijgewerkt');
    } catch (error) {
      alert('Fout bij updaten status');
    }
  };

  const handleDoorsturenNaarAfdeling = async () => {
    if (!selectedAfdeling) {
      alert('Selecteer eerst een afdeling');
      return;
    }

    try {
      await api.put(`/storingen/${id}`, {
        doorgestuurd_naar_afdeling: parseInt(selectedAfdeling),
        status: 'doorgestuurd'
      });
      setShowDoorsturenModal(false);
      setSelectedAfdeling('');
      loadData();
      alert('✅ Storing doorgestuurd!');
    } catch (error) {
      alert('Fout bij doorsturen');
    }
  };

  const handleGoedkeuren = async () => {
    try {
      await api.put(`/storingen/${id}`, { status: 'goedgekeurd' });
      setStoring({ ...storing, status: 'goedgekeurd' });
      alert('✅ Storing goedgekeurd!');
    } catch (error) {
      alert('Fout bij goedkeuren');
    }
  };

  const handleAfkeuren = async () => {
    const reden = prompt('Reden voor afkeuring:');
    if (!reden) return;
    
    try {
      await api.put(`/storingen/${id}`, { status: 'afgekeurd' });
      setStoring({ ...storing, status: 'afgekeurd' });
      alert('✅ Storing afgekeurd');
    } catch (error) {
      alert('Fout bij afkeuren');
    }
  };

const handleMaakWerkorder = async () => {
    const ordernummer = window.prompt('Ordernummer invoeren (optioneel):');
    if (ordernummer === null) return; // cancelled
    if (!window.confirm('Werkorder aanmaken van deze storing?')) return;
    try {
      const werkorderData = {
        titel: storing.titel,
        beschrijving: `[STORING] ${storing.beschrijving}\n\nGemeld door: ${storing.gemeld_door_voornaam} ${storing.gemeld_door_achternaam}\nLocatie: ${storing.locatie_adres || 'Geen locatie'}`,
        prioriteit: storing.prioriteit,
        locatie_adres: storing.locatie_adres || '',
        afdeling_id: gebruiker.afdeling_id,
        ordernummer: ordernummer || null,
        klant_id: storing.klant_id || null
      };
      const werkorderRes = await api.post('/werkorders', werkorderData);
      const werkorderId = werkorderRes.data.werkorder.id;
      await api.put(`/storingen/${id}`, {
        werkorder_id: werkorderId,
        status: 'werkorder_aangemaakt'
      });
      alert('✅ Werkorder aangemaakt!');
      navigate(`/werkorder/${werkorderId}`);
    } catch (error) {
      console.error('Fout bij aanmaken werkorder:', error);
      alert('Fout bij aanmaken werkorder');
    }
  };

  const handleVerwijderen = async () => {
    if (!window.confirm('Weet je zeker dat je deze storing wilt verwijderen? Dit kan niet ongedaan gemaakt worden.')) return;
    try {
      await api.delete(`/storingen/${id}`);
      alert('✅ Storing verwijderd');
      navigate('/storingen');
    } catch (error) {
      console.error('Fout bij verwijderen:', error);
      alert('Fout bij verwijderen storing');
    }
  };
  const openInMaps = () => {
    if (storing.locatie_lat && storing.locatie_lng) {
      window.open(`https://www.google.com/maps?q=${storing.locatie_lat},${storing.locatie_lng}`, '_blank');
    } else if (storing.locatie_adres) {
      window.open(`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(storing.locatie_adres)}`, '_blank');
    }
  };

  // === PERMISSIE LOGICA ===
  const myAfdelingId = Number(gebruiker?.afdeling_id);
  const gemeldDoorAfdeling = Number(storing?.gemeld_door_afdeling_id);
  const doorgestuurdNaar = Number(storing?.doorgestuurd_naar_afdeling);

  // Is dit gemeld door mijn afdeling?
  const isGemeldDoorMijnAfdeling = gemeldDoorAfdeling === myAfdelingId;
  
  // Is dit doorgestuurd naar mijn afdeling (van een andere afdeling)?
  const isOntvangenStoring = doorgestuurdNaar === myAfdelingId && gemeldDoorAfdeling !== myAfdelingId;
  
  // Is dit mijn storing die NIET is doorgestuurd naar een andere afdeling?
  const isMijnStoringNietDoorgestuurd = isGemeldDoorMijnAfdeling && (!doorgestuurdNaar || doorgestuurdNaar === myAfdelingId);
  
  // Is dit mijn storing die WEL is doorgestuurd naar een andere afdeling? (alleen lezen)
  const isMijnStoringDoorgestuurd = isGemeldDoorMijnAfdeling && doorgestuurdNaar && doorgestuurdNaar !== myAfdelingId;

  // Mag ik de status wijzigen?
  const magStatusWijzigen = isMijnStoringNietDoorgestuurd || isOntvangenStoring;
  
  // Mag ik doorsturen?
  const magDoorsturen = isMijnStoringNietDoorgestuurd;
  
  // Mag ik werkorder maken?
  const magWerkorderMaken = isMijnStoringNietDoorgestuurd || (isOntvangenStoring && storing?.status === 'goedgekeurd');

  // Filter afdelingen voor doorsturen (niet eigen afdeling)
  const doorstuurAfdelingen = alleAfdelingen.filter(a => Number(a.id) !== myAfdelingId);

  if (loading) {
    return <div className="loading-screen">Laden...</div>;
  }

  if (!storing) {
    return <div className="error-screen">Storing niet gevonden</div>;
  }

  return (
    <div className="storing-detail">

      <div className="container">
        <div className="header">
          <button onClick={() => navigate('/storingen')} className="btn-back">← Terug</button>
          <h1>🚨 {storing.titel}</h1>
          {isOntvangenStoring && (
            <span style={{
              background: '#10b981',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              marginLeft: '10px'
            }}>
              📥 Ontvangen van {storing.melder_afdeling_naam}
            </span>
          )}
          {isMijnStoringDoorgestuurd && (
            <span style={{
              background: '#f59e0b',
              color: 'white',
              padding: '4px 12px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600',
              marginLeft: '10px'
            }}>
              📤 Verzonden naar {storing.afdeling_naam}
            </span>
          )}
        </div>

        <div className="detail-grid">
          <div className="detail-main">
            {/* Status knoppen */}
            {magStatusWijzigen && (
              <div className="info-card">
                <h3>Status wijzigen</h3>
                <div className="status-buttons">
                  <button
                    onClick={() => handleStatusChange('nieuw')}
                    className={`btn-status ${storing.status === 'nieuw' ? 'active' : ''}`}
                  >
                    🆕 Nieuw
                  </button>
                  <button
                    onClick={() => handleStatusChange('in_behandeling')}
                    className={`btn-status ${storing.status === 'in_behandeling' ? 'active' : ''}`}
                  >
                    🔧 In behandeling
                  </button>
                  <button
                    onClick={() => handleStatusChange('opgelost')}
                    className={`btn-status ${storing.status === 'opgelost' ? 'active' : ''}`}
                  >
                    ✅ Opgelost
                  </button>
                </div>
              </div>
            )}

            <div className="info-card">
              <h3>📝 Beschrijving</h3>
              <p>{storing.beschrijving}</p>
            </div>

            <div className="info-card">
              <h3>ℹ️ Informatie</h3>
              <div className="info-rows">
                <div className="info-row">
                  <span className="info-label">Gemeld door:</span>
                  <span>👤 {storing.gemeld_door_voornaam} {storing.gemeld_door_achternaam}</span>
                </div>
                {storing.melder_afdeling_naam && (
                  <div className="info-row">
                    <span className="info-label">Van afdeling:</span>
                    <span>🏢 {storing.melder_afdeling_naam}</span>
                  </div>
                )}
                <div className="info-row">
                  <span className="info-label">Prioriteit:</span>
                  <span>{storing.prioriteit}</span>
                </div>
                <div className="info-row">
                  <span className="info-label">Status:</span>
                  <span style={{
                    background: storing.status === 'goedgekeurd' ? '#10b981' : 
                               storing.status === 'afgekeurd' ? '#ef4444' :
                               storing.status === 'doorgestuurd' ? '#f59e0b' :
                               storing.status === 'werkorder_aangemaakt' ? '#06b6d4' : '#6b7280',
                    color: 'white',
                    padding: '2px 8px',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}>
                    {storing.status}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Gemeld op:</span>
                  <span>{new Date(storing.created_at).toLocaleString('nl-NL')}</span>
                </div>
                {storing.afdeling_naam && storing.doorgestuurd_naar_afdeling && (
                  <div className="info-row">
                    <span className="info-label">Doorgestuurd naar:</span>
                    <span>➡️ {storing.afdeling_naam}</span>
                  </div>
                )}
                {storing.werkorder_titel && (
                  <div className="info-row">
                    <span className="info-label">Werkorder:</span>
                    <span>📋 {storing.werkorder_titel}</span>
                  </div>
                )}
              </div>
            </div>

            {storing.locatie_adres && (
              <div className="info-card">
                <h3>📍 Locatie</h3>
                <p>{storing.locatie_adres}</p>
                {(storing.locatie_lat || storing.locatie_lng) && (
                  <small style={{ color: '#64748b', display: 'block', marginTop: '8px' }}>
                    GPS: {storing.locatie_lat}, {storing.locatie_lng}
                  </small>
                )}
                <button onClick={openInMaps} className="btn-maps">
                  🗺️ Open in Google Maps
                </button>
              </div>
            )}

            {fotos.length > 0 && (
              <div className="info-card">
                <h3>📸 Foto's ({fotos.length})</h3>
                <div className="foto-grid">
                  {fotos.map(foto => (
                    <img
                      key={foto.id}
                      src={foto.url || `/uploads/storingen/${foto.bestand_naam}`}
                      alt="Storing foto"
                      className="foto-thumb"
                      onClick={() => window.open(foto.url || `/uploads/storingen/${foto.bestand_naam}`, '_blank')}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="detail-sidebar">
            <div className="info-card">
              <h3>🔧 Acties</h3>
              
              {/* Goedkeuren/Afkeuren - alleen voor ontvangen storingen die nog niet verwerkt zijn */}
              {isOntvangenStoring && (storing.status === 'doorgestuurd' || storing.status === 'nieuw') && (
                <>
                  <button
                    onClick={handleGoedkeuren}
                    className="btn-action"
                    style={{ background: '#10b981' }}
                  >
                    ✅ Goedkeuren
                  </button>
                  <button
                    onClick={handleAfkeuren}
                    className="btn-action"
                    style={{ background: '#ef4444' }}
                  >
                    ❌ Afkeuren
                  </button>
                </>
              )}

              {/* Doorsturen - alleen voor eigen storingen die niet doorgestuurd zijn */}
              {magDoorsturen && doorstuurAfdelingen.length > 0 && (
                <button
                  onClick={() => setShowDoorsturenModal(true)}
                  className="btn-action"
                >
                  📤 Doorsturen naar Afdeling
                </button>
              )}

              {/* Werkorder maken */}
              {magWerkorderMaken && (
                <button
                  onClick={handleMaakWerkorder}
                  className="btn-action"
                  disabled={storing.werkorder_id}
                >
                  {storing.werkorder_id ? '✅ Werkorder aangemaakt' : '📋 Maak Werkorder'}
                </button>
              )}
              {/* Verwijderen */}
              <button
                onClick={handleVerwijderen}
                className="btn-action"
                style={{ background: '#dc2626' }}
              >
                🗑️ Verwijderen
              </button>

              {/* Alleen-lezen melding voor doorgestuurde storingen */}
              {isMijnStoringDoorgestuurd && (
                <div style={{ 
                  background: '#fef3c7', 
                  border: '1px solid #f59e0b',
                  borderRadius: '8px',
                  padding: '12px',
                  marginTop: '10px'
                }}>
                  <p style={{ color: '#92400e', fontSize: '14px', margin: 0 }}>
                    📤 Deze storing is doorgestuurd naar <strong>{storing.afdeling_naam}</strong>. 
                    Je kunt de status volgen maar niet meer bewerken.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Doorsturen Modal */}
      {showDoorsturenModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            padding: '30px',
            borderRadius: '12px',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ marginBottom: '20px' }}>📤 Doorsturen naar Afdeling</h3>
            
            <select
              value={selectedAfdeling}
              onChange={(e) => setSelectedAfdeling(e.target.value)}
              style={{
                width: '100%',
                padding: '12px',
                borderRadius: '8px',
                border: '1px solid #d1d5db',
                marginBottom: '20px',
                fontSize: '16px'
              }}
            >
              <option value="">-- Selecteer afdeling --</option>
              {doorstuurAfdelingen.map(afd => (
                <option key={afd.id} value={afd.id}>{afd.naam}</option>
              ))}
            </select>

            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => { setShowDoorsturenModal(false); setSelectedAfdeling(''); }}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: 'white',
                  cursor: 'pointer'
                }}
              >
                Annuleren
              </button>
              <button
                onClick={handleDoorsturenNaarAfdeling}
                disabled={!selectedAfdeling}
                style={{
                  flex: 1,
                  padding: '12px',
                  borderRadius: '8px',
                  border: 'none',
                  background: selectedAfdeling ? '#3b82f6' : '#d1d5db',
                  color: 'white',
                  cursor: selectedAfdeling ? 'pointer' : 'not-allowed'
                }}
              >
                Doorsturen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StoringDetail;
